import Papa from "papaparse";
import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { AiFillFileExcel, AiFillFilePdf } from "react-icons/ai";
import { MdCloudUpload } from "react-icons/md";
import cross from "../../../images/Popup/Cross.svg";
import Toggle from "../../../utils/Toggle/Toggle";
import Backdrop from "./Backdrop";
import { CategorySelector } from "./CategorySelector";
import styles from "./Dialog.module.css";

const uploadTypes = [
  {
    label: "Upload from PDF",
    value: "pdf",
  },
  {
    label: "Paste Link",
    value: "link",
  },
  {
    label: "Upload from CSV",
    value: "csv",
  },
];

const MAX_PDF_SIZE_BYTES = 7 * 1024 * 1024;

const FormFields = [
  {
    title: "Catalogue Name",
    placeholder: "Enter Name",
    type: "text",
    value: "name",
  },
  {
    title: "Brand",
    placeholder: "Enter Brand",
    type: "text",
    value: "brand",
  },
  {
    title: "Category",
    placeholder: "Enter Category",
    type: "category",
    value: "category",
  },

  {
    title: "Margin",
    placeholder: "0.0",
    type: "number",
    value: "discount",
  },
  {
    title: "Show",
    type: "toggle",
    value: "show",
  },
];

export const CreateCatalogDialog = (props) => {
  const [uploadType, setUploadType] = useState(uploadTypes[0].value);
  const [formFields, setFormFields] = useState({
    name: "",
    brand: "",
    category: "",
    discount: 0.0,
    show: false,
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const [pdfFile, setPdfFile] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [links, setLinks] = useState([""]);

  const handleChange = (field, value) => {
    setFormFields((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formFields.name || formFields.name.trim() === "") {
      newErrors.name = "Catalogue name is required";
    }

    if (!formFields.brand || formFields.brand.trim() === "") {
      newErrors.brand = "Brand is required";
    }

    if (!formFields.category || formFields.category === "") {
      newErrors.category = "Category is required";
    }

    if (
      formFields.discount === "" ||
      formFields.discount < 0 ||
      formFields.discount > 100
    ) {
      newErrors.discount = "Discount must be between 0 and 100";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    setSubmitted(true);

    if (!validateForm()) {
      return;
    }

    let fileToUpload = null;
    let linksToUpload = [];

    if (!!pdfFile) {
      fileToUpload = pdfFile;
    }
    if (links.length > 0) {
      linksToUpload.push(...links.filter((link) => link.trim() !== ""));
    }

    const submitData = (finalLinks) => {
      if (props.onSubmit) {
        props.onSubmit({
          ...formFields,
          file: fileToUpload,
          links: finalLinks.filter((link) => link.trim() !== ""),
          uploadType: uploadType,
        });
      }

      props.switch();
      setSubmitted(false);
      setErrors({});
      setPdfFile(null);
      setCsvFile(null);
      setLinks([""]);
    };

    if (!!csvFile) {
      Papa.parse(csvFile, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          console.log({ data: results.data });
          const csvLinks = results.data.flatMap((row) => {
            const values = Object.values(row);
            const keys = Object.keys(row);
            return [...values, ...keys].filter(
              (item) => typeof item === "string" && item.trim() !== ""
            );
          });
          linksToUpload.push(...csvLinks);
          console.log(linksToUpload);
          submitData(linksToUpload);
        },
      });
      return;
    }

    submitData(linksToUpload);
  };
  return props.show ? (
    <div>
      <Backdrop show={props.show} switch={props.switch} />
      <div className={styles.popup}>
        <div className={styles.cross}>
          <img src={cross} alt="" onClick={props.switch}></img>
        </div>
        <div className={styles.formContainer}>
          <div className={styles.heading}>Catalogue Information</div>
          {FormFields.map((field) => (
            <Input
              key={field.value}
              title={field.title}
              value={formFields[field.value]}
              onChange={(value) => handleChange(field.value, value)}
              placeholder={field.placeholder}
              type={field.type}
              fieldName={field.value}
              error={errors[field.value]}
              submitted={submitted}
            />
          ))}
          <Uploader
            type={uploadType}
            onTypeChange={(v) => {
              setUploadType(v);
              setPdfFile(null);
              setCsvFile(null);
              setLinks([""]);
            }}
            pdfFile={pdfFile}
            setPdfFile={setPdfFile}
            csvFile={csvFile}
            setCsvFile={setCsvFile}
            links={links}
            setLinks={setLinks}
          />
          <button className={styles.confirmButton} onClick={handleConfirm}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  ) : null;
};

const Input = ({
  title,
  value,
  onChange,
  placeholder,
  type,
  fieldName,
  error,
  submitted,
}) => {
  return (
    <div className={styles.fieldWrapper}>
      <div className={styles.fieldContainer}>
        <div className={styles.field}>{title}:</div>
        {type === "text" || type === "number" ? (
          fieldName === "discount" ? (
            <div className={styles.inputWithSuffix}>
              <input
                className={`${styles.input} ${
                  error && submitted ? styles.inputError : ""
                }`}
                name={fieldName}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                type={type}
              />
              <span className={styles.suffix}>%</span>
            </div>
          ) : (
            <input
              className={`${styles.input} ${
                error && submitted ? styles.inputError : ""
              }`}
              name={fieldName}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              type={type}
            />
          )
        ) : type === "toggle" ? (
          <div className={styles.inputToggle}>
            <div>{value === true ? "True" : "False"}</div>
            <Toggle
              status={value}
              setStatus={(newValue) => onChange(newValue)}
            />
          </div>
        ) : type === "category" ? (
          <CategorySelector
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            fieldName={fieldName}
            error={error}
            submitted={submitted}
            styles={styles}
          />
        ) : null}
      </div>
      {error && submitted && <div className={styles.errorMessage}>{error}</div>}
    </div>
  );
};

const Uploader = ({
  type,
  onTypeChange,
  pdfFile,
  setPdfFile,
  csvFile,
  setCsvFile,
  links,
  setLinks,
}) => {
  const [pdfError, setPdfError] = useState("");

  useEffect(() => {
    setPdfError("");
  }, [type]);

  const handleAddLink = () => {
    setLinks([...links, ""]);
  };

  const handleLinkChange = (index, value) => {
    const newLinks = [...links];
    newLinks[index] = value;
    setLinks(newLinks);
  };

  const {
    getRootProps: getPdfRootProps,
    getInputProps: getPdfInputProps,
    isDragActive: isPdfDragActive,
  } = useDropzone({
    accept: {
      "application/pdf": [".pdf"],
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        if (file.size > MAX_PDF_SIZE_BYTES) {
          setPdfError("PDF must be 7MB or smaller");
          setPdfFile(null);
          return;
        }
        setPdfError("");
        setPdfFile(file);
      }
    },
  });

  const {
    getRootProps: getCsvRootProps,
    getInputProps: getCsvInputProps,
    isDragActive: isCsvDragActive,
  } = useDropzone({
    accept: {
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setCsvFile(acceptedFiles[0]);
      }
    },
  });

  return (
    <div className={styles.uploaderContainer}>
      <div className={styles.radioGroup}>
        {uploadTypes.map((uploadType) => (
          <label key={uploadType.value} className={styles.radioLabel}>
            <input
              type="radio"
              name="uploadType"
              value={uploadType.value}
              checked={type === uploadType.value}
              onChange={(e) => onTypeChange(e.target.value)}
              className={styles.radioInput}
            />
            <span className={styles.radioText}>{uploadType.label}</span>
          </label>
        ))}
      </div>

      {type === "pdf" && (
        <div
          {...getPdfRootProps()}
          className={`${styles.dropZone} ${
            isPdfDragActive ? styles.dropZoneActive : ""
          }`}
        >
          <input {...getPdfInputProps()} />
          {pdfFile ? (
            <div className={styles.fileDisplay}>
              <AiFillFilePdf size={48} color="#dc3545" />
              <div className={styles.fileName}>{pdfFile.name}</div>
            </div>
          ) : (
            <>
              <div className={styles.uploadIcon}>
                <MdCloudUpload size={48} />
              </div>
              <div className={styles.dropText}>
                {isPdfDragActive
                  ? "Drop the PDF here"
                  : "Drag and drop a PDF here"}
              </div>
            </>
          )}
        </div>
      )}
      {pdfError && (
        <div className={`${styles.errorMessage} ${styles.pdfErrorMessage}`}>
          {pdfError}
        </div>
      )}

      {type === "link" && (
        <div className={styles.linkContainer}>
          {links.map((link, index) => (
            <input
              key={index}
              type="url"
              className={styles.linkInput}
              placeholder="Enter Amazon URL of the Product"
              value={link}
              onChange={(e) => handleLinkChange(index, e.target.value)}
            />
          ))}
          <button
            type="button"
            className={styles.addButton}
            onClick={handleAddLink}
          >
            Add Another
          </button>
        </div>
      )}

      {type === "csv" && (
        <div
          {...getCsvRootProps()}
          className={`${styles.dropZone} ${
            isCsvDragActive ? styles.dropZoneActive : ""
          }`}
        >
          <input {...getCsvInputProps()} />
          {csvFile ? (
            <div className={styles.fileDisplay}>
              <AiFillFileExcel size={48} color="#28a745" />
              <div className={styles.fileName}>{csvFile.name}</div>
            </div>
          ) : (
            <>
              <div className={styles.uploadIcon}>
                <MdCloudUpload size={48} />
              </div>
              <div className={styles.dropText}>
                {isCsvDragActive
                  ? "Drop the Excel file here"
                  : "Drag and drop Excel file here"}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
